import { apiClient } from './axios';

import type { ScenarioChannel, ScenarioJudgeOutput } from '@repo/shared-types';

export type { ScenarioChannel, ScenarioJudgeOutput };

export type ScenarioRunStatus = 'QUEUED' | 'RUNNING' | 'JUDGED' | 'FAILED' | 'CANCELLED';
export type ScenarioRunTargetKind = 'live' | 'branch' | 'commit';
export type ScenarioCaseResult = 'pass' | 'fail' | 'inconclusive';

export const ACTIVE_SCENARIO_RUN_STATUSES = new Set<ScenarioRunStatus>(['QUEUED', 'RUNNING']);

/** Human labels keyed by `${transport}/${modality}`. */
export const SCENARIO_CHANNEL_LABELS: Record<string, string> = {
  'PSTN/VOICE': 'Phone (voice)',
  'WEBRTC/VOICE': 'WebRTC (voice)',
  'WHATSAPP/TEXT': 'WhatsApp (text)',
  'WHATSAPP/VOICE': 'WhatsApp (voice)',
  'WEB_WIDGET/TEXT': 'Web chat (text)',
  'WEB_WIDGET/VOICE': 'Widget (voice)',
  'EMAIL/TEXT': 'Email (text)',
};

export const channelKey = (channel: Pick<ScenarioChannel, 'transport' | 'modality'>): string =>
  `${channel.transport}/${channel.modality}`;

export const channelLabel = (channel: Pick<ScenarioChannel, 'transport' | 'modality'>): string =>
  SCENARIO_CHANNEL_LABELS[channelKey(channel)] ?? channelKey(channel);

export interface ScenarioRun {
  id: string;
  configuration_id: string;
  target_kind: ScenarioRunTargetKind;
  branch_id: string | null;
  commit_id: string | null;
  pipeline_run_id: string | null;
  status: ScenarioRunStatus;
  total: number;
  passed: number;
  failed: number;
  inconclusive: number;
  error: string | null;
  created_by?: string | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  updated_at?: string;
}

export interface ScenarioRunCase {
  id: string;
  scenario_id: string | null;
  scenario_name: string;
  transport: ScenarioChannel['transport'];
  modality: ScenarioChannel['modality'];
  status: string;
  result: ScenarioCaseResult | null;
  test_call_id: string | null;
  judge_output: ScenarioJudgeOutput | null;
}

export interface ScenarioRunDetail extends ScenarioRun {
  cases: ScenarioRunCase[];
}

export interface ScenarioAssignmentSummary {
  id: string;
  scenario_id: string;
  configuration_id: string;
  transport: ScenarioChannel['transport'];
  modality: ScenarioChannel['modality'];
  required: boolean;
  enabled: boolean;
  scenario: { id: string; name: string };
}

export type ScenarioGateStatus = 'pass' | 'fail' | 'not_run' | 'disabled';

export interface ScenarioGate {
  enabled: boolean;
  commit_id: string | null;
  status: ScenarioGateStatus;
  required: Array<{
    scenario_id: string;
    scenario_name: string;
    transport: ScenarioChannel['transport'];
    modality: ScenarioChannel['modality'];
    result: ScenarioCaseResult | 'not_run';
  }>;
  last_run_id: string | null;
}

export interface CreateScenarioRunInput {
  configurationId: string;
  target: { kind: ScenarioRunTargetKind; branchId?: string; commitId?: string };
  scenarioIds?: string[];
  channels?: ScenarioChannel[];
}

export const scenarioRunsApi = {
  list: async (configurationId?: string) =>
    (
      await apiClient.get<ScenarioRun[]>('/agent-improvement/scenario-runs', {
        params: configurationId ? { configuration_id: configurationId } : undefined,
      })
    ).data,

  get: async (id: string) =>
    (await apiClient.get<ScenarioRunDetail>(`/agent-improvement/scenario-runs/${id}`)).data,

  create: async (input: CreateScenarioRunInput) =>
    (await apiClient.post<ScenarioRun>('/agent-improvement/scenario-runs', input)).data,

  cancel: async (id: string) =>
    (await apiClient.post<ScenarioRun>(`/agent-improvement/scenario-runs/${id}/cancel`)).data,

  rerunFailed: async (id: string) =>
    (await apiClient.post<ScenarioRun>(`/agent-improvement/scenario-runs/${id}/rerun-failed`)).data,

  adjust: async (id: string) =>
    (
      await apiClient.post<{ pipeline_run_id: string }>(
        `/agent-improvement/scenario-runs/${id}/adjust`,
      )
    ).data,

  assignments: async (configurationId: string) =>
    (
      await apiClient.get<ScenarioAssignmentSummary[]>('/agent-improvement/scenario-assignments', {
        params: { configuration_id: configurationId },
      })
    ).data,

  gate: async (configurationId: string, commitId?: string) =>
    (
      await apiClient.get<ScenarioGate>(`/agent-improvement/scenario-gate/${configurationId}`, {
        params: commitId ? { commit_id: commitId } : undefined,
      })
    ).data,
};
